import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface WaveAccount {
  full_name: string;
  wave_number: string;
}

interface WithdrawalFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  availableEarnings: number;
  userId: string;
}

const WithdrawalForm = ({ isOpen, onClose, onSuccess, availableEarnings, userId }: WithdrawalFormProps) => {
  const [loading, setLoading] = useState(false);
  const [hasExistingAccount, setHasExistingAccount] = useState<boolean | null>(null);
  const [accountChoice, setAccountChoice] = useState<'existing' | 'new'>('existing');
  const [waveAccount, setWaveAccount] = useState<WaveAccount>({ full_name: '', wave_number: '' });
  const [existingAccount, setExistingAccount] = useState<WaveAccount | null>(null);
  const { toast } = useToast();

  // Check if user has made previous withdrawals
  useEffect(() => {
    if (isOpen && userId) {
      checkExistingAccount();
    }
  }, [isOpen, userId]);

  const checkExistingAccount = async () => {
    try {
      const { data, error } = await supabase
        .from('withdrawal_requests')
        .select('payment_details')
        .eq('user_id', userId)
        .eq('payment_method', 'wave')
        .not('payment_details', 'is', null)
        .order('requested_at', { ascending: false })
        .limit(1);

      if (error) throw error;

      if (data && data.length > 0 && data[0].payment_details) {
        const paymentDetails = data[0].payment_details as any;
        setExistingAccount({
          full_name: paymentDetails.full_name,
          wave_number: paymentDetails.wave_number
        });
        setHasExistingAccount(true);
      } else {
        setHasExistingAccount(false);
      }
    } catch (error) {
      console.error('Error checking existing account:', error);
      setHasExistingAccount(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (accountChoice === 'new' || !hasExistingAccount) {
      if (!waveAccount.full_name.trim() || !waveAccount.wave_number.trim()) {
        toast({
          title: "Missing Information",
          description: "Please fill in both full name and WAVE number.",
          variant: "destructive",
        });
        return;
      }
    }

    setLoading(true);

    try {
      const paymentDetails = accountChoice === 'existing' && existingAccount 
        ? existingAccount 
        : waveAccount;

      // Create withdrawal request
      const { error } = await supabase
        .from('withdrawal_requests')
        .insert({
          user_id: userId,
          amount: availableEarnings,
          payment_method: 'wave',
          payment_details: paymentDetails as any,
          status: 'pending'
        });

      if (error) throw error;

      // Update available earnings to 0
      const { error: profileError } = await supabase
        .from('profiles')
        .update({ available_earnings: 0 })
        .eq('id', userId);

      if (profileError) throw profileError;

      toast({
        title: "Withdrawal Request Submitted",
        description: "Your withdrawal request has been submitted and will be processed within 2-3 business days.",
      });

      onSuccess();
      onClose();
    } catch (error) {
      console.error('Error submitting withdrawal:', error);
      toast({
        title: "Withdrawal Failed",
        description: "There was an error processing your withdrawal request. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setAccountChoice('existing');
    setWaveAccount({ full_name: '', wave_number: '' });
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => {
      if (!open) {
        onClose();
        resetForm();
      }
    }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Withdraw Earnings</DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-sm text-green-800">
              <strong>Amount to withdraw:</strong> D{availableEarnings.toFixed(2)}
            </p>
          </div>

          {hasExistingAccount && existingAccount && (
            <div className="space-y-3">
              <Label className="text-base font-medium">WAVE Account</Label>
              <RadioGroup 
                value={accountChoice} 
                onValueChange={(value: 'existing' | 'new') => setAccountChoice(value)}
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="existing" id="existing" />
                  <Label htmlFor="existing" className="text-sm">
                    Use existing account: {existingAccount.full_name} ({existingAccount.wave_number})
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="new" id="new" />
                  <Label htmlFor="new" className="text-sm">Add new WAVE account</Label>
                </div>
              </RadioGroup>
            </div>
          )}

          {(accountChoice === 'new' || !hasExistingAccount) && (
            <div className="space-y-3">
              <div>
                <Label htmlFor="fullName">Full Name</Label>
                <Input
                  id="fullName"
                  type="text"
                  value={waveAccount.full_name}
                  onChange={(e) => setWaveAccount(prev => ({ ...prev, full_name: e.target.value }))}
                  placeholder="Enter your full name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="waveNumber">WAVE Number</Label>
                <Input
                  id="waveNumber"
                  type="text"
                  value={waveAccount.wave_number}
                  onChange={(e) => setWaveAccount(prev => ({ ...prev, wave_number: e.target.value }))}
                  placeholder="Enter your WAVE number"
                  required
                />
              </div>
            </div>
          )}

          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-xs text-blue-800">
              Your withdrawal will be processed within 2-3 business days and sent to your WAVE account.
            </p>
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                onClose();
                resetForm();
              }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading}
              className="flex-1 bg-green-600 hover:bg-green-700"
            >
              {loading ? 'Processing...' : 'Submit Request'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default WithdrawalForm;